import type {
    HalGeometry,
    HalImage,
    HalImageAddressMode,
    HalImageChannelLayout,
    HalPainterParametersType,
    HalPainterTarget,
    HalRef,
    ShaderReturnType,
} from "@cm/hal"
import {Matrix3x2, Matrix4, Size2, Vector2} from "@cm/math"
import {assertNever} from "@cm/utils"
import type {WebGl2Context} from "#webgl2/webgl2-context"
import {MAX_MIP_LEVELS, MAX_TEXTURE_UNITS, MAX_ATLAS_COUNT} from "#webgl2/constants"
import {getHalAddressMode} from "#webgl2/utils"
import {checkForGlError} from "#webgl2/helpers/gl-error"
import type {WebGl2Image} from "#webgl2/webgl2-image"
import {WebGl2ImagePhysical} from "#webgl2/webgl2-image-physical"
import {WebGl2ImageVirtual} from "#webgl2/webgl2-image-virtual"
import {resolveImage} from "#webgl2/webgl2-image-utils"
import {compileAndLinkProgram, getUniformLocation} from "#webgl2/webgl2-shader-utils"
import type {ViewTransformArgs} from "@cm/hal/hal-paintable"
import {WebGl2ShaderCodeGen} from "#webgl2/webgl2-shader-code-gen"
import {ATLAS_TEXTURE_UNIT_OFFSET} from "#webgl2/webgl2-shader-constants"
import {loggerFactory} from "@cm/browser-log"

/**
 * This class is shared between multiple WebGlLayerGeometry instances that use the same WebGlCanvas and shading-function.
 * The shading-function is a GLSL function with the signature "vec4 computeColor(vec2 worldPosition, vec2 uv, vec4 color)" and
 * can call functions with the signature "vec4 texelFetchN(uvec2 texelIndex)" to sample from bound textures, where N
 * ranges from 0 to MAX_TEXTURE_UNITS - 1 and texelIndex is in pixels.
 */

export class WebGl2Shader {
    constructor(
        readonly context: WebGl2Context,
        readonly shadingFunction: string,
        readonly returnType: ShaderReturnType,
        readonly conservativeRasterization: boolean,
    ) {
        logger.trace("Creating WebGl2Shader", shadingFunction)
        const vertexSrc = WebGl2ShaderCodeGen.genVertexShader({conservativeRasterization})
        const fragmentSrc = WebGl2ShaderCodeGen.genFragmentShader({shadingFunction, returnType})

        const gl = this.context.gl
        this.program = compileAndLinkProgram(gl, vertexSrc, fragmentSrc)
        this.locConservativeRasterizationPixelSize = conservativeRasterization
            ? getUniformLocation(gl, this.program, "u_conservativeRasterizationPixelSize")
            : null
        this.locConservativeRasterizationAddressMode = conservativeRasterization
            ? getUniformLocation(gl, this.program, "u_conservativeRasterizationAddressMode")
            : null
        this.locWorldTransform = this.getOptionalUniformLocation("u_worldTransform")
        this.locUVTransform = this.getOptionalUniformLocation("u_uvTransform")
        this.locTargetViewTransform = this.getOptionalUniformLocation("u_targetViewTransform")
        this.locTargetViewUvOffset = this.getOptionalUniformLocation("u_targetViewUvOffset")
        this.locTargetSize = this.getOptionalUniformLocation("u_targetSize")
        this.locTargetMipLevel = this.getOptionalUniformLocation("u_targetMipLevel")

        this.locViewMipLevelOffset = this.getOptionalUniformLocation("u_viewMipLevelOffset")
        this.locViewOffset = this.getOptionalUniformLocation("u_viewOffset")
        this.locViewSize = this.getOptionalUniformLocation("u_viewSize")

        this.locImageWrapOffset = this.getOptionalUniformLocation("u_imageWrapOffset")
        this.locImageWrapSize = this.getOptionalUniformLocation("u_imageWrapSize")
        this.locImageWrapGap = this.getOptionalUniformLocation("u_imageWrapGap")
        this.locNumChannels = this.getOptionalUniformLocation("u_numChannels")
        this.locNumMipLevels = this.getOptionalUniformLocation("u_numMipLevels")
        this.locShardSize = this.getOptionalUniformLocation("u_shardSize")
        this.locNumShards = this.getOptionalUniformLocation("u_numShards")
        this.locAtlasIndex = this.getOptionalUniformLocation("u_atlasIndex")

        this.locImage = []
        this.locAtlas = []
        for (let i = 0; i < MAX_TEXTURE_UNITS; i++) {
            this.locImage.push(this.getOptionalUniformLocation(`u_image${i}`))
        }
        for (let j = 0; j < MAX_ATLAS_COUNT; j++) {
            this.locAtlas.push(this.getOptionalUniformLocation(`u_atlas${j}`))
        }
        this.locAtlasShardSize = this.getOptionalUniformLocation("u_atlasShardSize")
        this.locAtlasNumShards = this.getOptionalUniformLocation("u_atlasNumShards")

        this.locPageLookupMipLevelInfo = this.getOptionalUniformLocation("u_pageLookupMipLevelInfo")
        this.locPageLookupWidth = this.getOptionalUniformLocation("u_pageLookupWidth")
        this.locModulationColor = this.getOptionalUniformLocation("u_modulationColor")
        this.locClearAlpha = this.getOptionalUniformLocation("u_clearAlpha")
    }

    // WebGlEntity
    dispose(): void {
        this.context.gl.deleteProgram(this.program)
    }

    getUniformLocation(program: WebGLProgram, uniformName: string): WebGLUniformLocation {
        return getUniformLocation(this.context.gl, this.program, uniformName)
    }

    getOptionalUniformLocation(uniformName: string): WebGLUniformLocation | null {
        try {
            return getUniformLocation(this.context.gl, this.program, uniformName)
        } catch (_error: unknown) {
            return null
        }
    }

    setUniforms(
        target: HalPainterTarget,
        targetMipLevel: number,
        worldTransform: Matrix3x2,
        uvTransform: Matrix3x2,
        geometry: HalGeometry,
        images: (WebGl2Image | undefined)[],
        modulationColor: [r: number, g: number, b: number, a: number],
        parameters?: HalPainterParametersType,
        conservativeRasterizationAddressMode?: HalImageAddressMode,
    ) {
        const getNumChannelByChannelLayout = (channelLayout: HalImageChannelLayout) => {
            switch (channelLayout) {
                case "RGBA":
                    return 4
                case "RGB":
                    return 3
                case "R":
                    return 1
                default:
                    assertNever(channelLayout)
            }
        }
        const getPageLookupMipLevelInfo = (image: HalImage | undefined): number[] => {
            let levelInfo: number[]
            if (image instanceof WebGl2ImageVirtual) {
                const mipLevelInfo = image.mipmapInfo
                levelInfo = mipLevelInfo.level.flatMap((info) => [info.firstPageIndex, info.numMipLevelPages.x, info.numMipLevelPages.y, 0])
            } else {
                levelInfo = []
            }
            while (levelInfo.length < MAX_MIP_LEVELS * 4) {
                levelInfo.push(0)
            }
            return levelInfo
        }
        const targetSize = [target.width, target.height]
        const targetPaintable = target
        const resolvedImages = images.map((image) => resolveImage(image))
        const viewMipLevelOffsets = images.flatMap((image) => image?.descriptor.mipLevelOffset ?? 0)
        const imageNumMipLevels = resolvedImages.map((image) => (image ? image.descriptor.numMipLevels : 0))
        const viewOffsets = images.flatMap((image) => Vector2.toArray(image?.descriptor.textureOffset ?? {x: 0, y: 0}))
        const viewSizes = images.flatMap((image) => (image ? [image.descriptor.width, image.descriptor.height] : [0, 0]))
        const imageSizes = resolvedImages.flatMap((image) => (image ? [image.descriptor.width, image.descriptor.height] : [0, 0]))
        const imageWrapOffset = images.flatMap((image) => Vector2.toArray(image?.descriptor.wrapOffset ?? {x: 0, y: 0}))
        const imageWrapSize = images.flatMap((image) => Size2.toArray(image?.descriptor.wrapSize ?? {width: 1, height: 1}))
        const imageWrapGap = images.flatMap((image) => Size2.toArray(image?.descriptor.wrapGap ?? {width: 0, height: 0}))
        const imageShardSizes = images.flatMap((image) => Size2.toArray(image?.descriptor.shardSize ?? {width: 0, height: 0}))
        const imageNumShards = images.flatMap((image) => Vector2.toArray(image?.descriptor.numShards ?? {x: 0, y: 0}))
        const imageNumChannels = images.map((image) => (image ? getNumChannelByChannelLayout(image.descriptor.channelLayout) : 0))
        const imageAtlasIndex = resolvedImages.flatMap((image) => (image instanceof WebGl2ImageVirtual ? 0 : -1))
        const pageLookupMipLevelInfo = resolvedImages.flatMap((image) => getPageLookupMipLevelInfo(image))
        const pageLookupWidth = resolvedImages.map((image) => (image instanceof WebGl2ImageVirtual ? image.mipmapInfo.pageLookupSize.width : 0))
        const gl = this.context.gl
        if (geometry.conservativeRasterization) {
            if (this.locConservativeRasterizationPixelSize) {
                const conservativeRasterizationPixelSize = 1
                gl.uniform1f(this.locConservativeRasterizationPixelSize, conservativeRasterizationPixelSize)
            }
            if (this.locConservativeRasterizationAddressMode) {
                conservativeRasterizationAddressMode ??= "clamp"
                if (conservativeRasterizationAddressMode !== "clamp" && conservativeRasterizationAddressMode !== "wrap") {
                    throw new Error(`Unsupported conservative rasterization address mode: ${conservativeRasterizationAddressMode}`)
                }
                gl.uniform1i(this.locConservativeRasterizationAddressMode, getHalAddressMode(conservativeRasterizationAddressMode))
            }
        }
        gl.uniformMatrix3x2fv(this.locWorldTransform, false, worldTransform.toArray())
        gl.uniformMatrix3x2fv(this.locUVTransform, false, uvTransform.toArray())
        if (this.locTargetSize) {
            gl.uniform2iv(this.locTargetSize, targetSize)
            checkForGlError(gl)
        }
        if (this.locTargetMipLevel) {
            gl.uniform1i(this.locTargetMipLevel, targetMipLevel)
            checkForGlError(gl)
        }
        if (this.locViewSize) {
            gl.uniform2iv(this.locViewSize, viewSizes)
            checkForGlError(gl)
        }
        if (this.locViewOffset) {
            gl.uniform2iv(this.locViewOffset, viewOffsets)
            checkForGlError(gl)
        }
        if (this.locViewMipLevelOffset) {
            gl.uniform1iv(this.locViewMipLevelOffset, viewMipLevelOffsets)
            checkForGlError(gl)
        }
        if (this.locImageWrapOffset) {
            gl.uniform2iv(this.locImageWrapOffset, imageWrapOffset)
            checkForGlError(gl)
        }
        if (this.locImageWrapSize) {
            gl.uniform2iv(this.locImageWrapSize, imageWrapSize)
            checkForGlError(gl)
        }
        if (this.locImageWrapGap) {
            gl.uniform2iv(this.locImageWrapGap, imageWrapGap)
            checkForGlError(gl)
        }
        if (this.locNumChannels) {
            gl.uniform1iv(this.locNumChannels, imageNumChannels)
            checkForGlError(gl)
        }
        if (this.locNumMipLevels) {
            gl.uniform1iv(this.locNumMipLevels, imageNumMipLevels)
            checkForGlError(gl)
        }
        if (this.locShardSize) {
            gl.uniform2iv(this.locShardSize, imageShardSizes)
            checkForGlError(gl)
        }
        if (this.locNumShards) {
            gl.uniform2iv(this.locNumShards, imageNumShards)
            checkForGlError(gl)
        }
        if (this.locAtlasIndex) {
            gl.uniform1iv(this.locAtlasIndex, imageAtlasIndex)
            checkForGlError(gl)
        }
        for (let i = 0; i < MAX_TEXTURE_UNITS; i++) {
            if (this.locImage[i]) {
                gl.uniform1i(this.locImage[i], i)
                checkForGlError(gl)
            }
        }
        for (let j = 0; j < MAX_ATLAS_COUNT; j++) {
            if (this.locAtlas[j]) {
                gl.uniform1i(this.locAtlas[j], ATLAS_TEXTURE_UNIT_OFFSET + j)
                checkForGlError(gl)
            }
        }
        const atlasDesc = this.context.atlasImageDescriptor
        if (atlasDesc) {
            if (this.locAtlasShardSize) {
                gl.uniform2i(this.locAtlasShardSize, atlasDesc.shardSize.width, atlasDesc.shardSize.height)
                checkForGlError(gl)
            }
            if (this.locAtlasNumShards) {
                gl.uniform2i(this.locAtlasNumShards, atlasDesc.numShards.x, atlasDesc.numShards.y)
                checkForGlError(gl)
            }
        }
        if (this.locPageLookupMipLevelInfo) {
            gl.uniform4iv(this.locPageLookupMipLevelInfo, pageLookupMipLevelInfo)
            checkForGlError(gl)
        }
        if (this.locPageLookupWidth) {
            gl.uniform1iv(this.locPageLookupWidth, pageLookupWidth)
            checkForGlError(gl)
        }
        if (this.locModulationColor) {
            gl.uniform4fv(this.locModulationColor, modulationColor)
            checkForGlError(gl)
        }
        if (this.locClearAlpha) {
            gl.uniform1i(this.locClearAlpha, targetPaintable.forceAlphaToOne ? 1 : 0)
            checkForGlError(gl)
        }
        if (parameters) {
            this.setParameters(parameters)
        }
    }

    setViewTransform(args: ViewTransformArgs) {
        const gl = this.context.gl
        gl.uniformMatrix3x2fv(this.locTargetViewTransform, false, Matrix3x2.fromMatrix3x2Like(args.viewTransform).toArray())
        gl.uniform2fv(this.locTargetViewUvOffset, Vector2.toArray(args.uvOffset))
    }

    setProgramAndData(geometry: HalGeometry, images: (WebGl2Image | undefined)[]) {
        const gl = this.context.gl
        gl.useProgram(this.program)
        for (let i = 0; i < MAX_TEXTURE_UNITS; i++) {
            const image = resolveImage(images[i])
            if (image) {
                if (image instanceof WebGl2ImagePhysical) {
                    const isIntFormat = image.descriptor.dataType === "uint8"
                    if (isIntFormat) {
                        gl.activeTexture(gl.TEXTURE0 + ATLAS_TEXTURE_UNIT_OFFSET + i)
                    } else {
                        gl.activeTexture(gl.TEXTURE0 + i)
                    }
                    gl.bindTexture(gl.TEXTURE_2D_ARRAY, image.texture)
                    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
                    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MIN_FILTER, gl.NEAREST_MIPMAP_NEAREST) // float textures require this filtering to be NEAREST
                    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
                    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)

                    if (isIntFormat) {
                        gl.activeTexture(gl.TEXTURE0 + i)
                    } else {
                        gl.activeTexture(gl.TEXTURE0 + ATLAS_TEXTURE_UNIT_OFFSET + i)
                    }
                    gl.bindTexture(gl.TEXTURE_2D_ARRAY, null)
                } else if (image instanceof WebGl2ImageVirtual) {
                    // Bind page table at source slot i
                    gl.activeTexture(gl.TEXTURE0 + i)
                    gl.bindTexture(gl.TEXTURE_2D_ARRAY, image.pageTableTexture.texture)
                    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
                    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MIN_FILTER, gl.NEAREST_MIPMAP_NEAREST)
                    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
                    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)

                    // Clear the atlas slot for this image index (atlases are bound globally below)
                    gl.activeTexture(gl.TEXTURE0 + ATLAS_TEXTURE_UNIT_OFFSET + i)
                    gl.bindTexture(gl.TEXTURE_2D_ARRAY, null)
                } else {
                    throw new Error("Invalid image type")
                }
            } else {
                gl.activeTexture(gl.TEXTURE0 + i)
                gl.bindTexture(gl.TEXTURE_2D_ARRAY, null)

                // Note: ATLAS_TEXTURE_UNIT_OFFSET + i is handled by the atlas binding loop below
            }
        }
        // Only bind atlas textures when virtual images are present as sources.
        // If no virtual image is used, atlas textures are not needed and must NOT be bound —
        // operations like copyToAtlas render INTO an atlas as the framebuffer target, and binding
        // the same texture as both framebuffer attachment and sampler creates a WebGL feedback loop.
        const hasVirtualImages = images.some((img) => resolveImage(img) instanceof WebGl2ImageVirtual)
        if (hasVirtualImages) {
            const atlases = this.context.atlases
            for (const atlas of atlases) {
                gl.activeTexture(gl.TEXTURE0 + ATLAS_TEXTURE_UNIT_OFFSET + atlas.id)
                gl.bindTexture(gl.TEXTURE_2D_ARRAY, atlas.image.texture)
                gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
                gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MIN_FILTER, gl.NEAREST_MIPMAP_NEAREST)
                gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
                gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
            }
            for (let j = atlases.length; j < MAX_ATLAS_COUNT; j++) {
                gl.activeTexture(gl.TEXTURE0 + ATLAS_TEXTURE_UNIT_OFFSET + j)
                gl.bindTexture(gl.TEXTURE_2D_ARRAY, null)
            }
        } else {
            // Ensure all atlas slots are unbound when not needed
            for (let j = 0; j < MAX_ATLAS_COUNT; j++) {
                gl.activeTexture(gl.TEXTURE0 + ATLAS_TEXTURE_UNIT_OFFSET + j)
                gl.bindTexture(gl.TEXTURE_2D_ARRAY, null)
            }
        }
    }

    unsetProgramAndData() {
        const gl = this.context.gl
        for (let i = 0; i < MAX_TEXTURE_UNITS; i++) {
            gl.activeTexture(gl.TEXTURE0 + i)
            gl.bindTexture(gl.TEXTURE_2D_ARRAY, null)
        }
        for (let j = 0; j < MAX_ATLAS_COUNT; j++) {
            gl.activeTexture(gl.TEXTURE0 + ATLAS_TEXTURE_UNIT_OFFSET + j)
            gl.bindTexture(gl.TEXTURE_2D_ARRAY, null)
        }
    }

    private setParameters(parameters: HalPainterParametersType) {
        const gl = this.context.gl
        Object.entries(parameters).forEach(([name, parameterValue]) => {
            const location = this.getOptionalUniformLocation(name)
            if (!location) {
                if (!parameterValue.isOptional) {
                    logger.warn("Parameter not found: " + name)
                }
            } else {
                switch (parameterValue.type) {
                    case "float": {
                        gl.uniform1f(location, parameterValue.value)
                        break
                    }
                    case "float2": {
                        gl.uniform2f(location, parameterValue.value.x, parameterValue.value.y)
                        break
                    }
                    case "float3": {
                        gl.uniform3f(location, parameterValue.value.x, parameterValue.value.y, parameterValue.value.z)
                        break
                    }
                    case "float4": {
                        gl.uniform4f(location, parameterValue.value.x, parameterValue.value.y, parameterValue.value.z, parameterValue.value.w)
                        break
                    }
                    case "int": {
                        gl.uniform1i(location, parameterValue.value)
                        break
                    }
                    case "int2": {
                        gl.uniform2i(location, parameterValue.value.x, parameterValue.value.y)
                        break
                    }
                    case "int3": {
                        gl.uniform3i(location, parameterValue.value.x, parameterValue.value.y, parameterValue.value.z)
                        break
                    }
                    case "int4": {
                        gl.uniform4i(location, parameterValue.value.x, parameterValue.value.y, parameterValue.value.z, parameterValue.value.w)
                        break
                    }
                    case "uint": {
                        gl.uniform1ui(location, parameterValue.value)
                        break
                    }
                    case "uint2": {
                        gl.uniform2ui(location, parameterValue.value.x, parameterValue.value.y)
                        break
                    }
                    case "uint3": {
                        gl.uniform3ui(location, parameterValue.value.x, parameterValue.value.y, parameterValue.value.z)
                        break
                    }
                    case "uint4": {
                        gl.uniform4ui(location, parameterValue.value.x, parameterValue.value.y, parameterValue.value.z, parameterValue.value.w)
                        break
                    }
                    case "float[]": {
                        gl.uniform1fv(location, parameterValue.value)
                        break
                    }
                    case "int[]": {
                        gl.uniform1iv(location, parameterValue.value)
                        break
                    }
                    case "uint[]": {
                        gl.uniform1uiv(location, parameterValue.value)
                        break
                    }
                    case "float2[]": {
                        gl.uniform2fv(
                            location,
                            parameterValue.value.flatMap((v) => [v.x, v.y]),
                        )
                        break
                    }
                    case "int2[]": {
                        gl.uniform2iv(
                            location,
                            parameterValue.value.flatMap((v) => [v.x, v.y]),
                        )
                        break
                    }
                    case "uint2[]": {
                        gl.uniform2uiv(
                            location,
                            parameterValue.value.flatMap((v) => [v.x, v.y]),
                        )
                        break
                    }
                    case "float3[]": {
                        gl.uniform3fv(
                            location,
                            parameterValue.value.flatMap((v) => [v.x, v.y, v.z]),
                        )
                        break
                    }
                    case "int3[]": {
                        gl.uniform3iv(
                            location,
                            parameterValue.value.flatMap((v) => [v.x, v.y, v.z]),
                        )
                        break
                    }
                    case "uint3[]": {
                        gl.uniform3uiv(
                            location,
                            parameterValue.value.flatMap((v) => [v.x, v.y, v.z]),
                        )
                        break
                    }
                    case "float4[]": {
                        gl.uniform4fv(
                            location,
                            parameterValue.value.flatMap((v) => [v.x, v.y, v.z, v.w]),
                        )
                        break
                    }
                    case "int4[]": {
                        gl.uniform4iv(
                            location,
                            parameterValue.value.flatMap((v) => [v.x, v.y, v.z, v.w]),
                        )
                        break
                    }
                    case "uint4[]": {
                        gl.uniform4uiv(
                            location,
                            parameterValue.value.flatMap((v) => [v.x, v.y, v.z, v.w]),
                        )
                        break
                    }
                    case "float3x2": {
                        const matrixValues = Matrix3x2.fromMatrix3x2Like(parameterValue.value).toArray()
                        gl.uniformMatrix3x2fv(location, false, matrixValues)
                        break
                    }
                    case "float4x4": {
                        const matrixValues = Matrix4.fromMatrix4Like(parameterValue.value).toArray()
                        gl.uniformMatrix4fv(location, false, matrixValues)
                        break
                    }
                    default:
                        assertNever(parameterValue)
                        break
                }
                checkForGlError(gl, `Setting parameter ${name}`)
            }
        })
    }

    private program: WebGLProgram
    private locConservativeRasterizationPixelSize: WebGLUniformLocation | null
    private locConservativeRasterizationAddressMode: WebGLUniformLocation | null
    private locWorldTransform: WebGLUniformLocation | null
    private locTargetViewTransform: WebGLUniformLocation | null
    private locTargetViewUvOffset: WebGLUniformLocation | null
    private locUVTransform: WebGLUniformLocation | null
    private locTargetSize: WebGLUniformLocation | null
    private locTargetMipLevel: WebGLUniformLocation | null
    private locImage: (WebGLUniformLocation | null)[]
    private locAtlas: (WebGLUniformLocation | null)[]
    private locAtlasShardSize: WebGLUniformLocation | null
    private locAtlasNumShards: WebGLUniformLocation | null
    private locViewMipLevelOffset: WebGLUniformLocation | null
    private locViewOffset: WebGLUniformLocation | null
    private locImageWrapOffset: WebGLUniformLocation | null
    private locImageWrapSize: WebGLUniformLocation | null
    private locImageWrapGap: WebGLUniformLocation | null
    private locViewSize: WebGLUniformLocation | null
    private locShardSize: WebGLUniformLocation | null
    private locNumShards: WebGLUniformLocation | null
    private locNumChannels: WebGLUniformLocation | null
    private locNumMipLevels: WebGLUniformLocation | null
    private locAtlasIndex: WebGLUniformLocation | null
    private locPageLookupMipLevelInfo: WebGLUniformLocation | null
    private locPageLookupWidth: WebGLUniformLocation | null
    private locModulationColor: WebGLUniformLocation | null
    private locClearAlpha: WebGLUniformLocation | null
}

export type WebGl2ShaderRef = HalRef<WebGl2Shader>

const logger = loggerFactory.getLogger("webgl2")
